import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Disc3 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

/**
 * Lands here after a successful social-OAuth callback on the backend.
 * Reads ?token=...&redirect=... — stores the JWT, primes the auth context,
 * cleans the URL, navigates to the intended app page.
 */
export default function OAuthSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const token = searchParams.get('token');
    const redirectPath = searchParams.get('redirect') || '/crate';

    if (!token) {
      toast.error('Sign-in failed — no token returned.');
      navigate('/login', { replace: true });
      return;
    }

    localStorage.setItem('groovlens_token', token);
    // Strip the token from the URL immediately so it isn't kept in history.
    window.history.replaceState({}, '', '/oauth/success');
    // Refresh /auth/me so the AuthProvider knows about the new user.
    refresh()
      .then(() => {
        toast.success('Signed in');
        navigate(redirectPath, { replace: true });
      })
      .catch(() => {
        toast.error('Could not verify session — please log in again.');
        localStorage.removeItem('groovlens_token');
        navigate('/login', { replace: true });
      });
  }, [searchParams, navigate, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="w-10 h-10 rounded-md bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))] animate-pulse">
          <Disc3 className="w-6 h-6" />
        </div>
        <div className="font-display text-sm">Finishing sign-in…</div>
      </div>
    </div>
  );
}
